import { LockOutlined, RocketLaunchOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Stack, Typography, useTheme } from '@mui/material';
import { useMicrospecDynamicUIContext } from '../GlobalProvider';
import { useMicrospecThemeLanguageContext } from '../Themes';
import { useOrganizationContext } from './OrganizationContext';

/**
 * ModuleNotEnabledForOrganization Component
 * 
 * Displays a message when a user tries to access a module that is not enabled
 * for their current organization. Provides clear guidance on how to enable the module.
 */
const ModuleNotEnabledForOrganization = ({ moduleName, modulePath }) => {
    const theme = useTheme();
    const { config } = useMicrospecDynamicUIContext();
    
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { selectedOrganization } = useOrganizationContext();
    
    const modulesManagementLink = config?.modules?.managementLink?.replace("{organization_id}", selectedOrganization?.id || "");

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                p: 3,
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Card
                elevation={3}
                sx={{
                    maxWidth: 600,
                    width: '100%',
                    textAlign: 'center',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[8],
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Stack gap={3} alignItems="center">
                        {/* Icon */}
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 167, 38, 0.15)'
                                    : 'rgba(255, 167, 38, 0.1)',
                                border: '2px solid',
                                borderColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 167, 38, 0.4)'
                                    : 'rgba(255, 167, 38, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                            }}
                        >
                            <LockOutlined sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                        </Box>

                        {/* Title */}
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                            {localizationProvider.translate({
                                type: 'Internal',
                                text: 'moduleNotEnabled'
                            })}
                        </Typography>

                        {/* Description */}
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            {localizationProvider.translate({
                                type: 'Internal',
                                text: 'moduleNotEnabledDescription'
                            })}
                        </Typography>

                        {/* Module Info */}
                        {moduleName && (
                            <Card
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    bgcolor: theme.palette.action.hover,
                                    borderColor: theme.palette.divider,
                                }}
                            >
                                <CardContent sx={{ py: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                            {localizationProvider.translate({
                                                type: 'Internal',
                                                text: 'requestedModule'
                                            })}
                                        </Typography>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.mode === 'dark' ?
                                                "#fff" :
                                                theme.palette.primary.main
                                        }}>
                                            {localizationProvider.translate(moduleName)}
                                        </Typography>
                                        {modulePath && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color: theme.palette.text.secondary,
                                                }}
                                            >
                                                {modulePath}
                                            </Typography>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* Organization Info */}
                        {selectedOrganization && (
                            <Box sx={{ width: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        {localizationProvider.translateByType({
                                            type: 'Internal',
                                            text: 'currentOrganization'
                                        })}:
                                    </Typography>
                                    <Chip
                                        label={selectedOrganization.name}
                                        size="small"
                                        sx={{
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.08)'
                                                : 'rgba(0, 0, 0, 0.08)',
                                            color: theme.palette.text.primary,
                                            borderColor: theme.palette.divider,
                                        }}
                                    />
                                </Stack>
                            </Box>
                        )}

                        {/* Instructions */}
                        <Box
                            sx={{
                                width: '100%',
                                p: 2,
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(33, 150, 243, 0.15)'
                                    : 'rgba(33, 150, 243, 0.1)',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark'
                                    ? 'rgba(33, 150, 243, 0.4)'
                                    : 'rgba(33, 150, 243, 0.3)',
                            }}
                        >
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                💡 {localizationProvider.translateByType({
                                    type: 'Internal',
                                    text: 'moduleEnableInstructions'
                                })}
                            </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Stack direction="row" gap={2} sx={{ mt: 2 }}>
                            {
                                modulesManagementLink && <Button
                                    variant="contained"
                                    startIcon={<RocketLaunchOutlined />}
                                    href={modulesManagementLink}
                                    target='_blank'
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                        }
                                    }}
                                >
                                    {localizationProvider.translateByType({
                                        type: 'Internal',
                                        text: 'goToModules'
                                    })}
                                </Button>
                            }
                            <Button
                                variant="outlined"
                                onClick={() => window.history.back()}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: theme.palette.divider,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}
                            >
                                {localizationProvider.translateByType({
                                    type: 'Internal',
                                    text: 'goBack'
                                })}
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ModuleNotEnabledForOrganization;
